import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { selectAuthToken } from 'selectors';
import type { AxiosResponse } from 'axios';
import { useNotification } from 'hooks/useNotification';
import { integrationQueryKeys } from './integrationQueryKeys';
import { HardDeleteParams, HardDeleteResponse } from 'IntegrationModule/types';
import { deleteIntegrationApi } from '../deleteIntegrationApi';

export const useDeleteIntegrationMutation = () => {
  const token = useSelector(selectAuthToken);
  const { showSuccess, showError } = useNotification();
  const queryClient = useQueryClient();

  const mutation = useMutation<
    AxiosResponse<HardDeleteResponse>,
    AxiosResponse<{ error: string; message: string; statusCode: number }>,
    HardDeleteParams
  >({
    mutationFn: async (params) => {
      if (!token) throw new Error('Not Authenticated');
      return deleteIntegrationApi({ params, token }) as Promise<AxiosResponse<HardDeleteResponse>>;
    },
    onSuccess: () => {
      showSuccess('Integration deleted');
      queryClient.invalidateQueries({ queryKey: integrationQueryKeys.lists() });
    },
    onError: (error) => {
      showError(`Failed to delete integration: ${error?.data?.message || 'Unknown error'}`);
    },
  });

  return {
    deleteIntegration: mutation.mutate,
    deleteIntegrationAsync: mutation.mutateAsync,
    isDeletingIntegration: mutation.isPending,
  };
};
